import { apiService, eventService } from "../api/client";
import type { TreeItem } from "../contexts/TreeContext";

type MonitorLoadAssignment = {
  id?: number | string | null;
  virtual_device_id?: number | string | null;
  virtual_device_name?: string | null;
  monitor_id?: number | string | null;
  monitor_label?: string | null;
  inventory_device_id?: number | string | null;
  inventory_device_name?: string | null;
  load_name?: string | null;
  assignment_name?: string | null;
  status?: string | null;
  category_id?: number | string | null;
  zone_id?: number | string | null;
  zone_name?: string | null;
  event_id?: number | string | null;
  event_name?: string | null;
  notes?: string | null;
};

const summarizeLoadAssignmentForTree = (
  load: MonitorLoadAssignment
): NonNullable<TreeItem["assignments"]>[number] => ({
  id: load.id,
  virtual_device_id: load.virtual_device_id,
  assignment_name: load.assignment_name,
  load_name: load.load_name,
  category_id: load.category_id,
  zone_id: load.zone_id,
  zone_name: load.zone_name,
  event_id: load.event_id,
  event_name: load.event_name,
  monitor_id: load.monitor_id,
  monitor_label: load.monitor_label,
  inventory_device_id: load.inventory_device_id,
  inventory_device_name: load.inventory_device_name,
  notes: load.notes,
});

const collectAssignedEventDevices = (items: TreeItem[]) => {
  const records: Array<{ virtualDeviceId: number; inventoryDeviceId: number }> = [];
  const visit = (item: TreeItem) => {
    if (item.node_type === "virtual_device") {
      const virtualDeviceId = Number(item.db_id);
      const inventoryDeviceId = Number(item.instance_id);
      if (Number.isFinite(virtualDeviceId) && Number.isFinite(inventoryDeviceId)) {
        records.push({ virtualDeviceId, inventoryDeviceId });
      }
    }
    (item.children ?? []).forEach(visit);
  };
  items.forEach(visit);
  return records;
};

export const enrichSingleEventTreeWithMonitorChildren = async (items: TreeItem[]) => {
  const eventDevices = collectAssignedEventDevices(items);
  if (eventDevices.length === 0) {
    return items;
  }

  const inventoryDeviceIds = new Set(eventDevices.map(({ inventoryDeviceId }) => inventoryDeviceId));
  const virtualDeviceIds = new Set(eventDevices.map(({ virtualDeviceId }) => virtualDeviceId));
  const [monitorsResponse, activeLoadsResponse] = await Promise.all([
    apiService.getAll("monitors", undefined, "display_order", "asc").catch(() => []),
    eventService.listLoadAssignments({ status: "active" }).catch(() => []),
  ]);

  const monitorsByInventoryDeviceId = new Map<
    number,
    Array<{
      id: number;
      label: string;
      key: string;
      kind: string;
      status: string;
      description?: string | null;
      display_order: number;
    }>
  >();

  (Array.isArray(monitorsResponse) ? monitorsResponse : [])
    .map((monitor: any) => ({
      id: Number(monitor.id),
      inventory_device_id: Number(monitor.inventory_device_id),
      label: String(monitor.label || `Monitor ${monitor.id}`),
      key: monitor.key,
      kind: monitor.kind,
      status: monitor.status,
      description: monitor.description,
      display_order: Number(monitor.display_order ?? 0),
    }))
    .filter((monitor) => Number.isFinite(monitor.id) && inventoryDeviceIds.has(monitor.inventory_device_id))
    .forEach((monitor) => {
      const bucket = monitorsByInventoryDeviceId.get(monitor.inventory_device_id) ?? [];
      bucket.push(monitor);
      monitorsByInventoryDeviceId.set(monitor.inventory_device_id, bucket);
    });

  monitorsByInventoryDeviceId.forEach((monitors, inventoryDeviceId) => {
    monitorsByInventoryDeviceId.set(
      inventoryDeviceId,
      [...monitors].sort((left, right) => {
        const orderDelta = left.display_order - right.display_order;
        if (orderDelta !== 0) return orderDelta;
        return left.label.localeCompare(right.label);
      })
    );
  });

  const activeLoadsByVirtualDeviceId = new Map<number, MonitorLoadAssignment[]>();
  (Array.isArray(activeLoadsResponse) ? activeLoadsResponse : [])
    .filter((load: MonitorLoadAssignment) => virtualDeviceIds.has(Number(load.virtual_device_id)))
    .forEach((load: MonitorLoadAssignment) => {
      const virtualDeviceId = Number(load.virtual_device_id);
      const bucket = activeLoadsByVirtualDeviceId.get(virtualDeviceId) ?? [];
      bucket.push(load);
      activeLoadsByVirtualDeviceId.set(virtualDeviceId, bucket);
    });

  const loadChildrenByVirtualDeviceId = new Map<number, TreeItem[]>();
  eventDevices.forEach(({ virtualDeviceId, inventoryDeviceId }) => {
    const monitors = monitorsByInventoryDeviceId.get(inventoryDeviceId) ?? [];
    if (monitors.length === 0) return;

    const activeLoads = activeLoadsByVirtualDeviceId.get(virtualDeviceId) ?? [];
    const activeLoadByMonitorId = new Map<number, MonitorLoadAssignment>();
    activeLoads.forEach((load: MonitorLoadAssignment) => {
      const monitorId = Number(load.monitor_id);
      if (Number.isFinite(monitorId)) {
        activeLoadByMonitorId.set(monitorId, load);
      }
    });
    if (monitors.length === 1 && activeLoadByMonitorId.has(monitors[0].id)) return;

    const monitorChildren = monitors
      .map((monitor) => {
        const activeLoad = activeLoadByMonitorId.get(monitor.id);
        if (!activeLoad) {
          return {
            id: `monitor-${virtualDeviceId}-${monitor.id}`,
            db_id: String(monitor.id),
            node_type: "monitor",
            className: "",
            label: monitor.label || `Monitor ${monitor.id}`,
            children: [],
            template_id: "",
            template_name: "",
            category_id: "",
            location_name: "",
            consumer_name: "",
            status: monitor.status || "online",
            description: monitor.description,
            instance_id: inventoryDeviceId,
            event_device_id: virtualDeviceId,
            monitor_key: monitor.key,
            monitor_kind: monitor.kind,
            assignments: [],
          } satisfies TreeItem;
        }
        return {
          id: `load-${activeLoad.id}`,
          db_id: String(activeLoad.id),
          node_type: "load_assignment",
          className: "",
          label: activeLoad.load_name || monitor.label || `Load ${activeLoad.id}`,
          children: [],
          template_id: "",
          template_name: "",
          category_id: "",
          location_name: "",
          consumer_name: "",
          status: activeLoad.status ?? "active",
          description: activeLoad.notes,
          instance_id: inventoryDeviceId,
          event_device_id: virtualDeviceId,
          monitor_key: monitor.key,
          monitor_kind: monitor.kind,
          assignments: [summarizeLoadAssignmentForTree(activeLoad)],
        } satisfies TreeItem;
      })
      .filter(Boolean) as TreeItem[];

    if (monitorChildren.length === 0) return;

    loadChildrenByVirtualDeviceId.set(virtualDeviceId, monitorChildren);
  });

  const applyChildren = (nodes: TreeItem[]): TreeItem[] =>
    nodes.map((item) => {
      const virtualDeviceId = item.node_type === "virtual_device" ? Number(item.db_id) : NaN;
      const loadChildren = Number.isFinite(virtualDeviceId)
        ? loadChildrenByVirtualDeviceId.get(virtualDeviceId)
        : undefined;
      return {
        ...item,
        children: loadChildren ?? applyChildren(item.children ?? []),
      };
    });

  return applyChildren(items);
};
